import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./arrastre.css";

/**
 * Ventanitas que se pueden mover (Esteban, 2026-10-13: "la ventanita de WappTeam no es movible y estorba para escribir";
 * igual el panel de la llamada). Se arrastran desde su barra; un clic sin mover sigue siendo clic. La posición se recuerda
 * por pestaña y navegador (localStorage) y nunca se sale de la pantalla. Doble clic en la barra la regresa a su lugar.
 *
 * ancla: dónde vive la ventanita sin mover ("abajo-derecha" = right/bottom fijos; "arriba-centro" = top fijo y left 50 %).
 * entera: la ventanita completa siempre a la vista (el panel de la llamada: si no, el botón de colgar queda afuera).
 *
 * El límite se mide con la caja real en pantalla, no con la esquina que dice el CSS (Kency, 2026-10-19: "no aparece la
 * llamada, no se puede cortar"): con el Marcador abierto el panel de la llamada se recorre 340 px a la izquierda y la
 * cuenta vieja lo dejaba soltar fuera de la pantalla; además la posición guardada solo se corregía al cambiar el tamaño
 * de la ventana, así que el panel salía invisible en cada llamada. Ahora se revisa cada vez que se pinta.
 */
type Ancla = "abajo-derecha" | "arriba-centro";
interface Pos { x: number; y: number; }

const leer = (llave: string): Pos => {
	try {
		const p = JSON.parse(localStorage.getItem("arrastre." + llave) || "");
		if (typeof p?.x === "number" && typeof p?.y === "number" && isFinite(p.x) && isFinite(p.y)) return p;
	} catch { /* sin almacenamiento */ }
	return { x: 0, y: 0 };
};

const MARGEN = 8;

/** Cuánto hay que mover una caja (dx, dy) para que quede dentro de la pantalla según la regla. */
const correccion = (r: { left: number; right: number; top: number; bottom: number; width: number; height: number }, entera: boolean): Pos => {
	const w = window.innerWidth, h = window.innerHeight;
	let dx = 0, dy = 0;
	if (entera) {
		if (r.width > w - 2 * MARGEN || r.left < MARGEN) dx = MARGEN - r.left;
		else if (r.right > w - MARGEN) dx = w - MARGEN - r.right;
		if (r.height > h - 2 * MARGEN || r.top < MARGEN) dy = MARGEN - r.top;
		else if (r.bottom > h - MARGEN) dy = h - MARGEN - r.bottom;
	} else {
		// Se puede esconder de lado, pero siempre quedan 120 px a la vista y la barra de arriba (de donde se agarra) adentro.
		const vis = Math.min(r.width, 120);
		if (r.right < vis) dx = vis - r.right;
		else if (r.left > w - vis) dx = w - vis - r.left;
		if (r.top < 0) dy = -r.top;
		else if (r.top > h - Math.min(r.height, 60)) dy = h - Math.min(r.height, 60) - r.top;
	}
	return { x: Math.round(dx), y: Math.round(dy) };
};

export const useArrastre = (llave: string, ancla: Ancla = "abajo-derecha", entera: boolean = false) => {
	const [pos, setPos] = useState<Pos>(() => leer(llave));
	const posRef = useRef(pos);
	posRef.current = pos;
	const movio = useRef(false);
	const caja = useRef<HTMLElement | null>(null);

	// Después de cada pintada: si quedó fuera de la pantalla (posición guardada en otra pantalla, Marcador abierto o
	// cerrado, ventana más chica), se mete otra vez. Solo mueve cuando hace falta, así que no se queda en un ciclo.
	useLayoutEffect(() => {
		const el = caja.current;
		if (!el) return;
		const r = el.getBoundingClientRect();
		if (!r.width && !r.height) return;
		const c = correccion(r, entera);
		if (Math.abs(c.x) > 1 || Math.abs(c.y) > 1) setPos((p) => ({ x: p.x + c.x, y: p.y + c.y }));
	});

	// Si la pantalla se achica, que no quede fuera (la revisión de arriba corre al volver a pintar).
	const [, repintar] = useState(0);
	useEffect(() => {
		const cambio = () => repintar((n) => n + 1);
		window.addEventListener("resize", cambio);
		return () => window.removeEventListener("resize", cambio);
	}, []);

	const onPointerDown = (e: React.PointerEvent) => {
		if (e.button !== 0) return;
		if ((e.target as HTMLElement).closest("input, textarea, select, [data-no-arrastre]")) return;
		const ini = { px: e.clientX, py: e.clientY, x: posRef.current.x, y: posRef.current.y };
		const r0 = caja.current?.getBoundingClientRect() || null;
		movio.current = false;
		const mover = (ev: PointerEvent) => {
			const dx = ev.clientX - ini.px, dy = ev.clientY - ini.py;
			if (!movio.current && Math.hypot(dx, dy) < 6) return;
			movio.current = true;
			// El límite con la caja como quedaría (la de al empezar, recorrida lo que se lleva arrastrado).
			const c = r0 ? correccion({ left: r0.left + dx, right: r0.right + dx, top: r0.top + dy, bottom: r0.bottom + dy, width: r0.width, height: r0.height }, entera) : { x: 0, y: 0 };
			setPos({ x: ini.x + dx + c.x, y: ini.y + dy + c.y });
		};
		const soltar = () => {
			window.removeEventListener("pointermove", mover);
			window.removeEventListener("pointerup", soltar);
			window.removeEventListener("pointercancel", soltar);
			try { localStorage.setItem("arrastre." + llave, JSON.stringify(posRef.current)); } catch { /* sin almacenamiento */ }
		};
		window.addEventListener("pointermove", mover);
		window.addEventListener("pointerup", soltar);
		window.addEventListener("pointercancel", soltar);
	};
	// Arrastrar no es hacer clic: el clic que sigue a un arrastre no abre ni cierra nada.
	const onClickCapture = (e: React.MouseEvent) => {
		if (movio.current) { e.preventDefault(); e.stopPropagation(); movio.current = false; }
	};
	const volver = () => {
		setPos({ x: 0, y: 0 });
		try { localStorage.removeItem("arrastre." + llave); } catch { /* sin almacenamiento */ }
	};

	const movida = pos.x !== 0 || pos.y !== 0;
	const transform = ancla === "arriba-centro" ? `translate(calc(-50% + ${pos.x}px), ${pos.y}px)` : movida ? `translate(${pos.x}px, ${pos.y}px)` : undefined;
	return {
		/** En el contenedor: ref + style (se suma a su propio estilo). */
		ref: (el: HTMLElement | null) => { caja.current = el; },
		style: transform ? { transform } : {},
		/** En la barra de donde se agarra. */
		agarre: { onPointerDown, onClickCapture, onDoubleClick: volver, "data-arrastrable": "1" },
		movida,
		/** De vuelta a su lugar (lo mismo que el doble clic). */
		volver,
	};
};
